/**
 * Standard Actions configuration for Dawn.
 *
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart,…}`). This file
 * overrides the bundle's built-in Dawn refresh path with an explicit,
 * in-theme version so forks that change Dawn's cart contract keep
 * working. Remove this file and the built-in defaults take over.
 *
 *   - openCart   — opens <cart-drawer>; falls back to /cart.
 *   - updateCart — after the Storefront API mutation, refreshes the
 *     affected cart sections and publishes `cart-update` so Dawn's
 *     pubsub subscribers react.
 *   - other actions (getCart, etc.) keep the default implementation.
 */

// Cart custom elements that advertise sections via getSectionsToRender().
// If Dawn adds a new cart custom element, add its tag here.
const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];

// Sections that Dawn's own pubsub subscribers refresh (cart.js's
// CartItems#onCartUpdate fetches and replaces these directly when
// cart-update fires; cart-drawer.js's renderContents handles the
// drawer body). We skip them here to avoid double-rendering.
// Format is '<element-tag>:<getSectionsToRender entry id>'.
// If you change which sections those subscribers refresh, update this set.
const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer:cart-drawer',
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

// Walk every mounted Dawn cart custom element, collect the sections it
// wants rendered, and dedupe. Returns a Map keyed by section id, each
// entry pointing at the DOM mount and the selector used to extract the
// fresh fragment.
function collectCartSections() {
  const sections = new Map();

  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try {
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }

    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;

      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;

      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;

      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;

      sections.set(sectionId, {
        mount,
        extractSelector: entry.selector || '.shopify-section',
      });
    }
  }

  return sections;
}

async function refreshDawnCartUI() {
  const sections = collectCartSections();
  const sectionsQuery = sections.size
    ? `?sections=${[...sections.keys()].join(',')}`
    : '';
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const url = `${cartUrl}.js${sectionsQuery}`;
  const cartData = await fetch(url, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);

  if (cartData?.sections) {
    for (const [id, { mount, extractSelector }] of sections) {
      const html = cartData.sections[id];
      if (!html) continue;
      const source = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(extractSelector);
      if (source) mount.replaceChildren(...source.childNodes);
    }
  }

  publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'external-refresh',
    cartData: cartData ?? undefined,
  });
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      return defaultHandler();
    },
  });

  actions.updateCart.configure({
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try {
        await refreshDawnCartUI();
      } catch (error) {
        console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
}

if (window.Shopify?.actions) {
  initStandardActions();
} else {
  document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });
}

(function installAowFastFlowersBasketGuard() {
  if (window.AOW_FAST_FLOWERS_BASKET_GUARD?.installed) return;

  const nativeFetch = window.fetch.bind(window);
  let decisionPromise = null;
  let switchPromise = null;

  const isYes = (value) => ['yes', 'true', '1'].includes(String(value || '').trim().toLowerCase());

  const readyLine = (item) => {
    const properties = item?.properties || {};
    return isYes(properties['_AOW Ready Stock'] || properties['AOW Ready Stock']);
  };

  const isFastFlowersCart = (cart) => {
    const attributes = cart?.attributes || {};
    return isYes(attributes['AOW Ready Stock']) || (Array.isArray(cart?.items) && cart.items.some(readyLine));
  };

  const knownFastFlowersSession = () => {
    if (window.AOW_CART_HAS_READY_STOCK_ITEMS === true || window.AOW_CART_READY_STOCK === true) return true;
    try { return Boolean(sessionStorage.getItem('aowReadyStockHoldId')); } catch (_) { return false; }
  };

  const isCartAddRequest = (input) => {
    try {
      const raw = typeof input === 'string' || input instanceof URL ? String(input) : input?.url;
      const url = new URL(raw || '', location.href);
      return url.origin === location.origin && (url.pathname === '/cart/add' || url.pathname === '/cart/add.js');
    } catch (_) { return false; }
  };

  const objectMarksFastFlowers = (value, depth = 0) => {
    if (!value || typeof value !== 'object' || depth > 6) return false;
    for (const [key, entry] of Object.entries(value)) {
      if (/aow ready stock/i.test(String(key)) && isYes(entry)) return true;
      if (entry && typeof entry === 'object' && objectMarksFastFlowers(entry, depth + 1)) return true;
    }
    return false;
  };

  const textMarksFastFlowers = (text) => {
    const raw = String(text || '');
    if (!/aow(?:%20|\s|\+)+ready(?:%20|\s|\+)+stock/i.test(raw)) return false;
    try { return objectMarksFastFlowers(JSON.parse(raw)); } catch (_) {}
    return /aow(?:%20|\s|\+)+ready(?:%20|\s|\+)+stock[^\r\n]{0,160}(?:yes|true|%59%65%73)/i.test(raw);
  };

  const bodyMarksFastFlowers = (body) => {
    if (!body) return false;
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      for (const [key, value] of body.entries()) {
        if (/aow ready stock/i.test(String(key)) && isYes(value)) return true;
      }
      return false;
    }
    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      for (const [key, value] of body.entries()) {
        if (/aow ready stock/i.test(String(key)) && isYes(value)) return true;
      }
      return false;
    }
    if (typeof body === 'string') return textMarksFastFlowers(body);
    return objectMarksFastFlowers(body);
  };

  const requestAddsFastFlowers = async (input, init = {}) => {
    if (bodyMarksFastFlowers(init?.body)) return true;
    if (typeof Request !== 'undefined' && input instanceof Request && !init?.body) {
      try { return textMarksFastFlowers(await input.clone().text()); } catch (_) { return false; }
    }
    return false;
  };

  const currentCart = async () => {
    const response = await nativeFetch('/cart.js?_=' + Date.now(), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!response.ok) throw new Error('Current basket could not be checked.');
    return response.json();
  };

  const blockedResponse = (status, description) => new Response(JSON.stringify({
    status,
    message: description,
    description,
  }), {
    status,
    headers: { 'Content-Type': 'application/json' },
  });

  const ensureModal = () => {
    let modal = document.getElementById('AowFastFlowersSwitchModal');
    if (modal) return modal;

    if (!document.getElementById('AowFastFlowersSwitchStyles')) {
      const style = document.createElement('style');
      style.id = 'AowFastFlowersSwitchStyles';
      style.textContent = '#AowFastFlowersSwitchModal{position:fixed;inset:0;z-index:10000;display:grid;place-items:center;padding:20px;background:rgba(18,36,29,.72);backdrop-filter:blur(5px)}#AowFastFlowersSwitchModal[hidden]{display:none}#AowFastFlowersSwitchModal .aow-ff-switch-card{width:min(620px,100%);border-radius:22px;background:#fff;padding:30px;box-shadow:0 24px 80px rgba(0,0,0,.25);color:#17372b}#AowFastFlowersSwitchModal .aow-ff-switch-badge{display:inline-flex;padding:6px 10px;border-radius:999px;background:#eef5eb;color:#345344;font-size:11px;font-weight:800;text-transform:uppercase}#AowFastFlowersSwitchModal h2{font-size:clamp(28px,5vw,38px);line-height:1.05;margin:14px 0 10px}#AowFastFlowersSwitchModal p{font-size:15px;line-height:1.6;color:#596a64;margin:0}#AowFastFlowersSwitchModal .aow-ff-switch-current{margin-top:15px;padding:12px 14px;border-radius:11px;background:#eef5eb;color:#334c40;font-size:13px;font-weight:700}#AowFastFlowersSwitchModal .aow-ff-switch-actions{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-top:22px}#AowFastFlowersSwitchModal button{border:1px solid #cdd7ce;border-radius:14px;background:#f8faf7;color:#17372b;padding:17px;text-align:left;cursor:pointer;font:inherit}#AowFastFlowersSwitchModal button strong{display:block;font-size:15px}#AowFastFlowersSwitchModal button span{display:block;margin-top:4px;font-size:12px;line-height:1.45;color:#607169}#AowFastFlowersSwitchModal button[data-aow-ff-switch]{border-color:#e2b8b2;background:#fff8f6;color:#743b33}@media(max-width:560px){#AowFastFlowersSwitchModal .aow-ff-switch-card{padding:22px}#AowFastFlowersSwitchModal .aow-ff-switch-actions{grid-template-columns:1fr}}';
      document.head.appendChild(style);
    }

    modal = document.createElement('div');
    modal.id = 'AowFastFlowersSwitchModal';
    modal.hidden = true;
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', 'AowFastFlowersSwitchTitle');
    modal.innerHTML = '<div class="aow-ff-switch-card"><span class="aow-ff-switch-badge">Fast Flowers basket</span><h2 id="AowFastFlowersSwitchTitle">This will change your Fast Flowers order</h2><p>Products from the rest of the store can’t be mixed with Fast Flowers. If you continue, your Fast Flowers delivery or collection promise and 5-minute stock reservation will be removed, and this basket will become a standard future order.</p><div class="aow-ff-switch-current" data-aow-ff-current></div><div class="aow-ff-switch-actions"><button type="button" data-aow-ff-keep><strong>Keep Fast Flowers basket</strong><span>Don’t add this product. Keep your current Fast Flowers order and reservation.</span></button><button type="button" data-aow-ff-switch><strong>Switch to standard order</strong><span>Remove the Fast Flowers basket and add this product as a normal future order.</span></button></div></div>';
    document.body.appendChild(modal);
    return modal;
  };

  const confirmSwitch = (cart) => {
    if (decisionPromise) return decisionPromise;
    decisionPromise = new Promise((resolve) => {
      const modal = ensureModal();
      const keep = modal.querySelector('[data-aow-ff-keep]');
      const change = modal.querySelector('[data-aow-ff-switch]');
      const current = modal.querySelector('[data-aow-ff-current]');
      const attributes = cart?.attributes || {};
      const fulfilment = String(attributes['AOW Ready Stock Fulfilment'] || 'Fast Flowers');
      const requested = String(attributes['AOW Requested Date'] || '').trim();
      current.textContent = requested ? `Current Fast Flowers option: ${fulfilment} · ${requested}` : `Current Fast Flowers option: ${fulfilment}`;

      const finish = (value) => {
        modal.hidden = true;
        document.removeEventListener('keydown', onKeyDown, true);
        resolve(value);
      };
      const onKeyDown = (event) => { if (event.key === 'Escape') finish(false); };
      keep.onclick = () => finish(false);
      change.onclick = () => finish(true);
      modal.onclick = (event) => { if (event.target === modal) finish(false); };
      document.addEventListener('keydown', onKeyDown, true);
      modal.hidden = false;
      window.setTimeout(() => keep.focus(), 0);
    }).finally(() => { decisionPromise = null; });
    return decisionPromise;
  };

  const blankFastFlowersAttributes = () => ({
    'AOW Event Date': '',
    'AOW Event Date ISO': '',
    'AOW Fulfilment Method': '',
    'AOW Requested Date': '',
    'AOW Date ISO': '',
    'AOW Collection Time': '',
    'AOW Ready Stock': '',
    'AOW Ready Stock Fulfilment': '',
    'AOW Ready Stock Cutoff Hour': '',
    'AOW Next Day': '',
    'AOW Ready Stock Hold': '',
    'AOW Ready Stock Hold Expires': '',
    'AOW Contains Plants': '',
    'Shipping-Date': '',
    'Checkout-Method': '',
  });

  const releaseAndClearFastFlowers = (cart) => {
    if (switchPromise) return switchPromise;
    switchPromise = (async () => {
      const attributes = cart?.attributes || {};
      let holdId = String(attributes['AOW Ready Stock Hold'] || '').trim();
      let releaseUrl = '';
      try {
        holdId = sessionStorage.getItem('aowReadyStockHoldId') || holdId;
        releaseUrl = sessionStorage.getItem('aowReadyStockReleaseUrl') || '';
      } catch (_) {}

      const updates = {};
      for (const item of Array.isArray(cart?.items) ? cart.items : []) {
        if (item?.key) updates[item.key] = 0;
      }

      const clearResponse = await nativeFetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates, attributes: blankFastFlowersAttributes() }),
      });
      if (!clearResponse.ok) throw new Error('Your Fast Flowers basket could not be changed. Please try again.');

      if (releaseUrl && holdId) {
        try {
          await nativeFetch(releaseUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ action: 'release', holdId }),
          });
        } catch (error) {
          console.warn('[AOW Fast Flowers guard] Reservation will expire normally because immediate release failed.', error);
        }
      }

      try {
        sessionStorage.removeItem('aowReadyStockHoldId');
        sessionStorage.removeItem('aowReadyStockHoldExpires');
        sessionStorage.removeItem('aowReadyStockReleaseUrl');
      } catch (_) {}
      window.AOW_CART_HAS_READY_STOCK_ITEMS = false;
      window.AOW_CART_READY_STOCK = false;
      window.AOW_CART_READY_STOCK_MIXED = false;
      window.AOW_READY_STOCK_FULFILMENT = '';
    })().finally(() => { switchPromise = null; });
    return switchPromise;
  };

  window.fetch = async function aowFastFlowersGuardedFetch(input, init = {}) {
    if (!isCartAddRequest(input)) return nativeFetch(input, init);
    if (await requestAddsFastFlowers(input, init)) return nativeFetch(input, init);

    let cart;
    try {
      cart = await currentCart();
    } catch (error) {
      console.warn('[AOW Fast Flowers guard] Basket check failed.', error);
      if (knownFastFlowersSession()) {
        return blockedResponse(503, 'We could not safely verify your Fast Flowers basket. Please try adding this product again.');
      }
      return nativeFetch(input, init);
    }

    if (!isFastFlowersCart(cart)) return nativeFetch(input, init);

    const shouldSwitch = await confirmSwitch(cart);
    if (!shouldSwitch) {
      return blockedResponse(422, 'Your Fast Flowers basket was kept. This standard product was not added.');
    }

    try {
      await releaseAndClearFastFlowers(cart);
    } catch (error) {
      return blockedResponse(409, error?.message || 'Your Fast Flowers basket could not be changed. Please try again.');
    }

    return nativeFetch(input, init);
  };

  window.AOW_FAST_FLOWERS_BASKET_GUARD = { installed: true };
})();
